<template>
  <Modal
    v-if="consents?.length > 0"
    ref="modal"
    v-model:open="open"
    :click-to-close="false"
    :esc-to-close="false"
    max-width="840px"
    max-height="80vh"
    width="90vw"
    :content-class="{ 'consent-modal': true }"
  >
    <PageTitle class="mt-0!" :title="t('consent_modal.title')" :description="t('consent_modal.subtitle')" />
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
      <div class="border-bdr-subtle lg:odd:border-r lg:odd:pr-6 lg:odd:last:border-r-0 lg:odd:last:pr-0">
        <div class="text-heading-section text-fgr-primary my-6">
          {{ t("consent_modal.personal_data.title") }}
        </div>
        <div class="flex flex-col gap-2">
          <div class="grid auto-cols-fr grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="text-body-default-m text-fgr-primary col-span-1">{{ t("first_name") }}</div>
            <InputText
              v-model="firstName"
              class="col-span-1 w-full"
              :error="nonValidKeys.includes('name') ? t('required') : undefined"
            />
          </div>
          <div class="grid auto-cols-fr grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="text-body-default-m text-fgr-primary col-span-1">{{ t("last_name") }}</div>
            <InputText
              v-model="lastName"
              class="col-span-1 w-full"
              :error="nonValidKeys.includes('name') ? t('required') : undefined"
            />
          </div>
          <div class="grid auto-cols-fr grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="text-body-default-m text-fgr-primary col-span-1">{{ t("phone") }}</div>
            <InputText
              v-model="information.phone"
              class="col-span-1 w-full"
              :error="nonValidKeys.includes('phone') ? t('required') : undefined"
            />
          </div>
          <div class="grid auto-cols-fr grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="text-body-default-m text-fgr-primary col-span-1">{{ t("email") }}</div>
            <InputText
              v-model="information.email"
              class="col-span-1 w-full"
              readonly
              :error="nonValidKeys.includes('email') ? t('required') : undefined"
            />
          </div>
          <div class="mt-4 grid auto-cols-fr grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="text-body-default-m text-fgr-primary col-span-1">
              {{ t("job_title") }}
            </div>
            <InputText
              v-model="information.work_title"
              class="col-span-1 w-full"
              is-required
              :error="nonValidKeys.includes('work_title') ? t('required') : undefined"
            />
          </div>
          <div class="grid auto-cols-fr grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="text-body-default-m text-fgr-primary col-span-1">
              {{ t("department") }}
            </div>
            <InputText
              v-model="information.department"
              class="col-span-1 w-full"
              is-required
              :error="nonValidKeys.includes('department') ? t('required') : undefined"
            />
          </div>
          <div class="mt-4 grid auto-cols-fr grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="text-body-default-m text-fgr-primary col-span-1">{{ t("default_weight_unit") }}</div>
            <Listbox
              v-model="weightUnit"
              :aria-label="t('default_weight_unit')"
              :options="[
                { label: `${t('gram')} (g)`, value: 'g' },
                { label: `${t('kilogram')} (kg)`, value: 'kg' },
                { label: `${t('pound')} (lb)`, value: 'lb' },
                { label: `${t('ounce')} (oz)`, value: 'oz' },
              ]"
            />
          </div>
        </div>
      </div>
      <div
        class="border-bdr-subtle border-t lg:border-t-0 lg:odd:border-r lg:odd:pr-6 lg:odd:last:border-r-0 lg:odd:last:pr-0"
      >
        <div class="text-heading-section text-fgr-primary my-6">
          {{ t("consent_modal.notifications.title") }}
        </div>
        <div class="flex flex-col gap-2">
          <Checkbox
            v-for="consent in consents"
            :key="consent.type"
            :model-value="!!consent.accepted"
            framed
            :readonly="consent.type === 'email_system_status'"
            :title="t(`consent_modal.notifications.${consent.type}.title`)"
            :subtitle="t(`consent_modal.notifications.${consent.type}.description`)"
            @update:model-value="(value: boolean) => (consent.accepted = value)"
          />
        </div>
      </div>
    </div>

    <Overlay v-if="isLoading" class="absolute inset-0" />
    <template #footer>
      <ModalFooter :text="t('consent_modal.footer_note')">
        <template #actions>
          <Button :variant="ButtonVariant.Primary" class="w-full sm:w-fit" :disabled="isLoading" @click="save">
            {{ t("save_information") }}
          </Button>
        </template>
      </ModalFooter>
    </template>
  </Modal>
</template>

<script setup lang="ts">
  import Checkbox from "~/components/Select/Checkbox.vue";
  import { toastAdd } from "~/utils/globalEvents";
  import type { Consent } from "~/types/consents";

  const { t } = useI18n();

  const INTERVAL_DAYS = 30;

  const { open, consents } = useConsentModal();
  const { user } = storeToRefs(useWorkspacesStore());
  const isLoading = ref(false);
  const hasAttemptedSave = ref(false);
  const weightUnit = ref<string>(unref(usePreferredWeightUnit()));

  const information = ref<
    IProfile & {
      work_title?: string;
      department?: string;
    }
  >({} as IProfile & { work_title?: string; department?: string });

  const nonValidKeys = computed(() => {
    if (!hasAttemptedSave.value) return [];
    const info = information.value;
    const keys: string[] = [];
    if (!info.name || info.name.trim() === "") {
      keys.push("name");
    }
    if (!info.work_title || info.work_title.trim() === "") {
      keys.push("work_title");
    }
    if (!info.department || info.department.trim() === "") {
      keys.push("department");
    }
    if (!info.email || info.email.trim() === "") {
      keys.push("email");
    }
    if (!info.phone || info.phone.trim() === "") {
      keys.push("phone");
    }
    return keys;
  });

  // Information
  const firstName = computed({
    get: () => information.value?.name?.split(" ")[0] || "",
    set: (value: string) => {
      const first = value;
      const last = information.value?.name?.split(" ")[1] || "";
      const combined = `${first} ${last}`.trim();
      information.value = {
        ...information.value,
        name: combined,
      };
    },
  });

  const lastName = computed({
    get: () => information.value?.name?.split(" ")[1] || "",
    set: (value: string) => {
      const first = information.value?.name?.split(" ")[0];
      const last = value;
      const combined = `${first} ${last}`.trim();

      information.value = {
        ...information.value,
        name: combined,
      };
    },
  });

  const fetchInformation = async () => {
    isLoading.value = true;
    return homeFetch("settings/profile")
      .then((response) => {
        if (response?.data) {
          information.value = { ...response.data?.profile } as IProfile;
          information.value.work_title = user.value?.work_title;
          information.value.department = user.value?.department;
        }
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const updateInformation = () => {
    isLoading.value = true;
    homeFetch("settings/profile", {
      method: "PUT",
      body: information.value,
    }).finally(() => {
      isLoading.value = false;
    });
  };

  const fetchConsents = () => {
    homeFetch("auth/consents").then((r) => {
      const c = (r.data?.consents as Consent[]) || [];

      // Filter consents that need action - Where latest accepted or rejected is more then INTERVAL_DAYS days ago or null
      const now = new Date();
      consents.value = c.filter((consent) => {
        // Auto-accept email_system_status if no action has been taken
        if (consent.type === "email_system_status" && !consent.accepted_at && !consent.rejected_at) {
          consent.accepted = true;
        }

        const acceptedAt = consent.accepted_at ? new Date(consent.accepted_at) : null;
        const rejectedAt = consent.rejected_at ? new Date(consent.rejected_at) : null;
        const latestActionDate =
          acceptedAt && rejectedAt ? (acceptedAt > rejectedAt ? acceptedAt : rejectedAt) : acceptedAt || rejectedAt;
        if (!latestActionDate) return true; // No action taken yet
        const diffTime = Math.abs(now.getTime() - latestActionDate.getTime());

        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > INTERVAL_DAYS;
      });

      // Open modal only after consents are fetched and filtered
      if (consents.value.length > 0) {
        open.value = true;
      }
    });
  };

  const updateConsents = () => {
    const payload = consents.value.map((consent) => ({
      key: consent.type,
      accepted: consent.accepted || false,
    }));

    return homeFetch("auth/consents", {
      method: "POST",
      body: { consents: payload },
    });
  };

  onMounted(() => {
    fetchConsents();
    fetchInformation();
  });

  // Watch for when the modal closes (e.g., user closes it)
  watch(open, (newValue) => {
    if (!newValue && consents.value.length > 0) {
      // If modal is closed but consents still need action, reopen after a short delay
      setTimeout(() => {
        open.value = true;
      }, 500);
    }
  });

  const save = async () => {
    hasAttemptedSave.value = true;
    if (nonValidKeys.value.length > 0) return;
    isLoading.value = true;
    try {
      await Promise.all([updateConsents(), updateInformation(), updateWeightUnit(weightUnit.value)]);
      open.value = false;
    } catch (error) {
      console.error("Failed to save consent information:", error);
      toastAdd({
        error: true,
        duration: 10000,
        text: t("consent_modal.save_error"),
      });
    } finally {
      isLoading.value = false;
    }
  };
</script>
